import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to close a form/modal when the browser back button is pressed.
 * Pushes a history entry when the form opens, and listens for popstate to close it.
 * @param {boolean} isOpen - Whether the form/modal is currently open
 * @param {Function} onClose - Function to call to close the form/modal
 */
export default function useBackButtonClose(isOpen, onClose) {
  const pushed = useRef(false);

  const stableClose = useCallback(() => {
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen && !pushed.current) {
      window.history.pushState({ formOpen: true }, '');
      pushed.current = true;
    }
    if (!isOpen && pushed.current) {
      pushed.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    const handlePopState = () => {
      if (pushed.current) {
        pushed.current = false;
        stableClose();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [stableClose]);
}
